import React, { useState } from 'react';
import { PublicEnvironmentVariables } from 'types';
import styles from './ConfigModal.module.css';
import { NavButton } from './common/NavButton';
import { Memory, Server, Toolbox } from './common/Icons';
import { EnvironmentSection } from './sections/EnvironmentSection';
import { CacheSection } from './sections/CacheSection';
import { UtilsSection } from './sections/UtilsSection';

interface ConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  environmentVariables: PublicEnvironmentVariables;
}

export function ConfigModal({
  isOpen,
  onClose,
  environmentVariables,
}: ConfigModalProps): React.JSX.Element | null {
  const [activeSection, setActiveSection] = useState<'env' | 'cache' | 'utils'>(
    'env'
  );

  function handleCloseOnEnter(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClose();
    }
  }

  return (
    <div
      className={`${styles.overlay} ${isOpen ? styles.overlayVisible : ''}`}
      onClick={onClose}
      role="button"
      tabIndex={0}
      onKeyDown={handleCloseOnEnter}
    >
      <div
        className={`${styles.modal} ${isOpen ? styles.modalVisible : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="button"
        tabIndex={0}
        onKeyDown={handleCloseOnEnter}
      >
        <button className={styles.closeButton} onClick={onClose} type="button">
          ×
        </button>
        <h2 className={styles.title}>Development Configuration</h2>

        <nav className={styles.nav}>
          <NavButton
            icon={<Server />}
            label="Environment"
            onClick={() => setActiveSection('env')}
            isActive={activeSection === 'env'}
          />
          <NavButton
            icon={<Memory />}
            label="Cache"
            onClick={() => setActiveSection('cache')}
            isActive={activeSection === 'cache'}
          />
          <NavButton
            icon={<Toolbox />}
            label="Utilities"
            onClick={() => setActiveSection('utils')}
            isActive={activeSection === 'utils'}
          />
        </nav>

        <div className={styles.content}>
          {activeSection === 'env' && (
            <EnvironmentSection environmentVariables={environmentVariables} />
          )}
          {activeSection === 'cache' && <CacheSection />}
          {activeSection === 'utils' && <UtilsSection />}
        </div>
      </div>
    </div>
  );
}
